// Grouping/association model for the panel board.
//
// Encapsulates the relationships between RCD groups, circuits (lines), and the
// protection devices that implement them, behind a narrow interface. Callers ask
// "what should light up if the user focuses X?" and receive a HighlightSet; they
// never touch the underlying join tables.
//
// Pure module: no React, no DOM, no API. Built from the editor's current local
// circuits/groups so renames and local group reassignment are reflected here.
//
// Relationships (from backend data):
//   circuit.protection_device_id → the MCB device that protects that line
//   group.protection_device_id   → the RCD device that heads that group
//   group.circuit_ids            → the lines that sit under that RCD

import type { Circuit, RCDGroup } from '@/types/api';

// What a single user interaction points at.
export type FocusTarget =
  | { kind: 'device'; id: string }
  | { kind: 'group'; id: string }
  | { kind: 'circuit'; id: string };

// Everything that should be highlighted across both the canvas (devices) and the
// groups/lines panel (groups, circuits) for the current focus.
export interface HighlightSet {
  readonly deviceIds: ReadonlySet<string>;
  readonly circuitIds: ReadonlySet<string>;
  readonly groupIds: ReadonlySet<string>;
}

export interface GroupingModel {
  readonly groups: readonly RCDGroup[];
  readonly circuits: readonly Circuit[];
  // Resolve the full cross-view highlight for a focus target (null → nothing).
  resolveHighlight(focus: FocusTarget | null): HighlightSet;
}

const EMPTY_HIGHLIGHT: HighlightSet = {
  deviceIds: new Set(),
  circuitIds: new Set(),
  groupIds: new Set(),
};

// Mutable accumulator used only while resolving a single highlight.
interface HighlightDraft {
  deviceIds: Set<string>;
  circuitIds: Set<string>;
  groupIds: Set<string>;
}

// Built from the editor's current circuits + groups (not raw BoardLayout) so local
// edits — renamed lines and reassigned groups — are reflected in highlighting.
export function buildGroupingModel(
  circuits: readonly Circuit[],
  groups: readonly RCDGroup[],
): GroupingModel {
  const circuitById = new Map<string, Circuit>();
  const groupById = new Map<string, RCDGroup>();
  const groupByCircuitId = new Map<string, RCDGroup>();
  // Reverse indexes from a protection device back to the line/group it implements.
  const circuitByDeviceId = new Map<string, Circuit>();
  const groupByDeviceId = new Map<string, RCDGroup>();

  for (const circuit of circuits) {
    circuitById.set(circuit.circuit_id, circuit);
    if (circuit.protection_device_id) {
      circuitByDeviceId.set(circuit.protection_device_id, circuit);
    }
  }
  for (const group of groups) {
    groupById.set(group.group_id, group);
    for (const circuitId of group.circuit_ids) {
      groupByCircuitId.set(circuitId, group);
    }
    if (group.protection_device_id) {
      groupByDeviceId.set(group.protection_device_id, group);
    }
  }

  // Add a line and its protecting device to the draft.
  function addCircuit(draft: HighlightDraft, circuit: Circuit | undefined): void {
    if (!circuit) return;
    draft.circuitIds.add(circuit.circuit_id);
    if (circuit.protection_device_id) draft.deviceIds.add(circuit.protection_device_id);
  }

  // Add one line plus its parent RCD group/RCD device, without adding sibling lines.
  function addCircuitContext(draft: HighlightDraft, circuit: Circuit | undefined): void {
    if (!circuit) return;
    addCircuit(draft, circuit);
    const group = groupByCircuitId.get(circuit.circuit_id);
    if (!group) return;
    draft.groupIds.add(group.group_id);
    if (group.protection_device_id) draft.deviceIds.add(group.protection_device_id);
  }

  // Add a group, its RCD device, and every member line (with each line's device).
  function addGroup(draft: HighlightDraft, group: RCDGroup | undefined): void {
    if (!group) return;
    draft.groupIds.add(group.group_id);
    if (group.protection_device_id) draft.deviceIds.add(group.protection_device_id);
    for (const circuitId of group.circuit_ids) {
      addCircuit(draft, circuitById.get(circuitId));
    }
  }

  function resolveHighlight(focus: FocusTarget | null): HighlightSet {
    if (!focus) return EMPTY_HIGHLIGHT;

    const draft: HighlightDraft = {
      deviceIds: new Set(),
      circuitIds: new Set(),
      groupIds: new Set(),
    };

    switch (focus.kind) {
      case 'group':
        addGroup(draft, groupById.get(focus.id));
        break;
      case 'circuit':
        addCircuitContext(draft, circuitById.get(focus.id));
        break;
      case 'device': {
        // An RCD device pulls in its whole group; an MCB device pulls in just its line.
        const group = groupByDeviceId.get(focus.id);
        if (group) {
          addGroup(draft, group);
        } else {
          const circuit = circuitByDeviceId.get(focus.id);
          if (circuit) addCircuitContext(draft, circuit);
          // Unrelated device (e.g. incoming breaker): highlight only itself.
          else draft.deviceIds.add(focus.id);
        }
        break;
      }
    }

    return draft;
  }

  return { groups, circuits, resolveHighlight };
}
